import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, CalendarDays, GraduationCap } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Icon from "@/components/Icon";
import {
  CardGridSkeleton,
  EmptyState,
  QueryBoundary,
} from "@/components/states";
import { useProgrammes } from "@/hooks/content";

const ALL = "All";

/** Format an ISO course date for display, tolerating missing/invalid values. */
function formatCourseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Featured training programmes, managed from the admin panel.
export default function FeaturedProgrammes() {
  const query = useProgrammes();
  const [cat, setCat] = useState(ALL);

  // Categories come from the published programmes themselves, so the filter
  // never offers a category with nothing behind it. Depends on query.data
  // directly — react-query keeps that reference stable across renders.
  const categories = useMemo(() => {
    const seen = [];
    (query.data || []).forEach((p) => {
      if (p.category && !seen.includes(p.category)) seen.push(p.category);
    });
    return [ALL, ...seen];
  }, [query.data]);

  const programmes = query.data || [];
  const list =
    cat === ALL ? programmes : programmes.filter((p) => p.category === cat);

  return (
    <section className="section-pad bg-white" data-testid="programmes-section">
      <div className="container-sky">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Featured Training Programmes"
            title="Build a Safer, Better-Prepared Workforce"
            description="Sample programmes delivered as classroom, practical, site-based, corporate, supervisor and customised sessions."
          />
          <Link to="/services/wsh-training" className="btn-outline-sky shrink-0">
            View Training <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <QueryBoundary
          query={query}
          skeleton={<CardGridSkeleton count={6} className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" />}
          empty={
            <div className="mt-10">
              <EmptyState
                icon={GraduationCap}
                title="No programmes published yet"
                description="Training programmes will appear here once they are published from the admin panel."
              />
            </div>
          }
          hideOnError
        >
          {() => (
            <>
              {/* Filter — only worth showing when there is more than one category. */}
              {categories.length > 2 && (
                <div className="mt-10 flex flex-wrap gap-2.5">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCat(c)}
                      aria-pressed={cat === c}
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                        cat === c
                          ? "bg-brand-ink text-white shadow-md shadow-brand/25"
                          : "bg-surface text-navy hover:bg-brand/10"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}

              <motion.div layout className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence mode="popLayout">
                  {list.map((p) => {
                    const courseDate = formatCourseDate(p.courseDate);
                    return (
                      <motion.div
                        key={p.id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.35 }}
                        data-testid={`programme-card-${p.id}`}
                        className="group flex flex-col rounded-2xl border border-slate-100 bg-surface p-6 transition-all duration-500 hover:border-brand/30 hover:shadow-[0_16px_40px_rgba(10,126,201,0.1)]"
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                            <Icon name={p.icon} className="h-5 w-5" />
                          </span>
                          {p.category && (
                            <span className="rounded-full bg-white px-3 py-1 font-mono-tech text-[10px] uppercase tracking-wider text-brand-ink">
                              {p.category}
                            </span>
                          )}
                        </div>

                        <h3 className="mt-5 font-heading text-lg font-bold leading-snug text-navy">
                          {p.title}
                        </h3>
                        <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">
                          {p.description}
                        </p>

                        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-4 text-xs font-medium text-slate-500">
                          {p.duration && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-brand" /> {p.duration}
                            </span>
                          )}
                          {p.format && <span className="truncate">{p.format}</span>}
                          {courseDate && (
                            <span className="flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5 text-brand" /> {courseDate}
                            </span>
                          )}
                          {p.price ? (
                            <span className="font-semibold text-navy">{p.price}</span>
                          ) : null}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>

              {/* Filter matched nothing — distinct from "no programmes at all". */}
              {list.length === 0 && (
                <p className="mt-8 text-sm text-slate-500">
                  No programmes in this category.
                </p>
              )}
            </>
          )}
        </QueryBoundary>
      </div>
    </section>
  );
}
